import { Alert } from '@truecare/ui';
import type { CaregiverStatus } from '@truecare/schemas';
import { SUPPORT_EMAIL } from '../support-email';

interface Props {
  status: CaregiverStatus;
  suspensionReason?: string | null;
  applicationSubmittedAt?: string | null;
}

/**
 * Phase 5f-4 — caregiver status-aware banner.
 *
 * Sticky-ish banner above the /me caregiver sections. The banner
 * itself is rendered inside the normal page flow (not position:sticky)
 * for now — the goal is "first thing the caregiver sees on /me",
 * which the document-flow placement already accomplishes.
 *
 * Status → banner mapping:
 *   APPLYING       → warning, "complete + submit your application"
 *   UNDER_REVIEW   → info, "submitted, awaiting operator review"
 *   ACTIVE         → no banner (clean state)
 *   SUSPENDED      → danger, suspensionReason rendered verbatim
 *   TERMINATED     → danger, account-closed
 *
 * Per 5f-4 + 5f-1 lock: drives the sectioning logic on /me and gives
 * the caregiver context for "why can't I do X."
 */
export function StatusBanner({ status, suspensionReason, applicationSubmittedAt }: Props) {
  if (status === 'ACTIVE') return null;

  if (status === 'APPLYING') {
    return (
      <Alert tone="warning" title="Complete your application">
        Open <strong>Profile</strong> from the account menu (top-right) to upload your
        documents and fill in your details, then click <strong>Submit for review</strong>{' '}
        to send your application to TrueCare™ for vetting. The <strong>Docs</strong> tab
        shows your upload progress.
      </Alert>
    );
  }

  if (status === 'UNDER_REVIEW') {
    const submittedDate = applicationSubmittedAt
      ? new Date(applicationSubmittedAt).toLocaleDateString()
      : null;
    // Phase 5f audit r1 — banner copy must match actual behavior. The
    // 5f-1 lock + backend `caregiver_locked_for_review` 403 mean the
    // profile + credentials + compliance are read-only while UNDER_REVIEW.
    // Phase 5f-e round-1 audit closure opened the availability section
    // during review (it's not part of operator review); round-3 closure
    // updates this copy to match the per-section reality.
    return (
      <Alert tone="info" title="Application under review">
        {submittedDate
          ? `You submitted your application on ${submittedDate}. Our operations team is reviewing your credentials.`
          : 'Your application is being reviewed by our operations team.'}{' '}
        Your profile, credentials, and compliance documents are locked while
        the review is in progress. We'll email you when the review completes.
      </Alert>
    );
  }

  // Phase 5g (change-list 1.1) — approved, completing onboarding.
  if (status === 'ONBOARDING') {
    return (
      <Alert tone="info" title="You're approved — finish onboarding">
        Your credentials passed review. Complete the <strong>Get started</strong>{' '}
        checklist (agreement, SIN, and banking) and your account activates
        automatically so you can start receiving shift offers.
      </Alert>
    );
  }

  if (status === 'SUSPENDED') {
    return (
      <Alert tone="danger" title="Account suspended">
        Your account is currently suspended.{' '}
        {suspensionReason
          ? `Reason: ${suspensionReason}.`
          : 'Please contact TrueCare™ support for details.'}{' '}
        Reach us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </Alert>
    );
  }

  // TERMINATED
  return (
    <Alert tone="danger" title="Account closed">
      This account has been closed and can no longer take shifts. If you believe
      this was in error, contact{' '}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
    </Alert>
  );
}
